import { Link } from "react-router-dom";
import { useFeaturedArticles } from "../../../hooks/useFeaturedArticles";
import { toNewsItems } from "../../../lib/articleAdapter";
import type { NewsItem } from "../../../mocks/news";
import NewsCard from "./NewsCard";
import FeaturedCarousel from "./FeaturedCarousel";

function SidebarAdEmpty({ title, desc, accent = "blue" }: { title: string; desc: string; accent?: "blue" | "amber" }) {
  const accentClasses = {
    blue: "from-blue-500 to-blue-400",
    amber: "from-amber-500 to-amber-400",
  };

  return (
    <Link
      to="/publicidad"
      className="relative rounded-xl overflow-hidden border border-dashed border-slate-200 group cursor-pointer hover:border-red-400/50 hover:bg-slate-100 transition-all duration-300 flex-1 flex flex-col"
      style={{ minHeight: "180px" }}
    >
      <div className="flex-1 flex flex-col items-center justify-center text-center p-5">
        <div className="w-11 h-11 flex items-center justify-center bg-slate-200 rounded-xl mb-3 group-hover:bg-red-500/20 transition-colors">
          <i className="ri-megaphone-line text-slate-400 text-lg group-hover:text-red-400 transition-colors" />
        </div>
        <p className="text-[#0B1F3A] font-semibold text-sm" style={{ fontFamily: "'Sora', sans-serif" }}>
          {title}
        </p>
        <p className="text-slate-400 text-[10px] mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {desc}
        </p>
        <span
          className="inline-flex items-center gap-1 mt-3 text-red-400 text-[10px] font-semibold"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          Anunciar aquí <i className="ri-arrow-right-line text-xs" />
        </span>
      </div>
      <div className={`h-1 bg-gradient-to-r ${accentClasses[accent]} w-full`} />
    </Link>
  );
}

interface FeaturedNewsProps {
  excludeIds?: number[];
}

export default function FeaturedNews({ excludeIds }: FeaturedNewsProps) {
  const { articles, loading } = useFeaturedArticles({ excludeIds });
  const featured = toNewsItems(articles);

  const heroItems = featured.slice(0, 3);
  const firstSide = featured[3];
  const restSide = featured.slice(4);

  if (loading) {
    return (
      <section className="bg-[#F4F7FB] py-4 md:py-8">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="animate-pulse space-y-4 md:space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/3" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
              <div className="lg:col-span-2 h-[480px] bg-slate-200 rounded-2xl" />
              <div className="h-[420px] lg:h-[480px] bg-slate-200 rounded-2xl" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (featured.length === 0) {
    return (
      <section id="noticias-destacadas" className="bg-[#F4F7FB] py-4 md:py-8">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-4 md:mb-8">
            <div className="flex items-center gap-4">
              <div className="w-1 h-9 bg-[#0B1F3A] rounded-full" />
              <h2
                className="text-[#0B1F3A] font-extrabold"
                style={{ fontFamily: "'Sora', sans-serif", fontSize: "1.75rem" }}
              >
                Noticias Destacadas
              </h2>
            </div>
          </div>
          <div className="text-center py-12 bg-slate-100 rounded-2xl border border-slate-200">
            <div className="w-14 h-14 flex items-center justify-center bg-slate-200 rounded-full mx-auto mb-4">
              <i className="ri-newspaper-line text-slate-400 text-2xl" />
            </div>
            <p className="text-slate-500 text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Próximamente artículos destacados
            </p>
            <Link
              to="/actualidad"
              className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-[#0B1F3A] text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Ver todas las noticias <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="noticias-destacadas" className="bg-[#F4F7FB] py-4 md:py-8">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        {/* Section header */}
        <div className="flex items-center justify-between mb-4 md:mb-8">
          <div className="flex items-center gap-4">
            <div className="w-1 h-9 bg-[#0B1F3A] rounded-full" />
            <h2
              className="text-[#0B1F3A] font-extrabold"
              style={{ fontFamily: "'Sora', sans-serif", fontSize: "1.75rem" }}
            >
              Noticias Destacadas
            </h2>
          </div>
          <Link
            to="/actualidad"
            className="hidden sm:flex items-center gap-2 text-slate-500 text-sm font-semibold hover:text-[#0B1F3A] transition-colors cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Ver todas <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Main grid — carousel + 1 side card */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Main feature — carousel */}
          <div className="md:col-span-1 lg:col-span-2 flex flex-col">
            <div className="flex-1">
              <FeaturedCarousel items={heroItems} />
            </div>
          </div>

          {/* Side — single card */}
          <div className="flex flex-col">
            {firstSide ? (
              <div className="flex-1">
                <NewsCard item={firstSide} variant="default" />
              </div>
            ) : (
              <SidebarAdEmpty
                title="¿Tu empresa en Aviación Insight RD?"
                desc="Llega a miles de lectores especializados"
                accent="blue"
              />
            )}
          </div>
        </div>

        {/* Bottom row — 3 cards centered */}
        {restSide.length > 0 && (
          <div className="flex flex-wrap justify-center gap-4 md:gap-5 mt-4 md:mt-6">
            {restSide.slice(0, 3).map((item: NewsItem) => (
              <div key={item.id} className="w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-16px)] min-w-0">
                <NewsCard item={item} variant="compact" />
              </div>
            ))}
          </div>
        )}

        {/* Mobile "Ver todas" */}
        <div className="mt-4 md:mt-8 text-center sm:hidden">
          <Link
            to="/actualidad"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#0B1F3A] text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Ver todas las noticias <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}